<script setup lang="ts">
import { computed, inject, onBeforeUnmount, ref, watch } from 'vue';
import api from '@/api';
import { trackingSurveyEvent } from '@/api/tracking';
import { FILE_NAME_SEARCH_API_DEBOUNCE_WAIT_MS } from '@/constants';
import { DgrDialog, DgrIcon, DgrLoading } from '@stockmarkteam/donguri-ui';
import { isAxiosError } from 'axios';
import SurveySimpleUserDocumentItem from '@/components/survey/document/survey-simple-user-document-item.vue';
import { FileNameSearchResponse, SpecifiedMethod } from '@/types';
import type { SurveyPageName } from '@/types';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { useUserDocumentActionHistories } from '@/utils/composables/useUserDocumentActionHistories';
import { debounce } from '@/utils/debounce';
import { specifiedMethodKey } from '@/utils/injectionKeys';
import { useTeamInfo } from '@/utils/swr';
import { userSession } from '@/utils/userSession';

interface Props {
  sessionId?: string; // surveysテーブルのuuidカラムの値 (要約画面のURLに含まれるID)。深堀り検索（同じスレッド）の間は同じID
  pageName: SurveyPageName;
}

/**
 * Props
 */

const props = defineProps<Props>();

/**
 * Emits
 */

const emit = defineEmits<{
  'select-file': [];
}>();

/**
 * Local State
 */

const SPECIFIED_FILES_LIMIT = 10;
const PAGING_LIMIT = 20;
const { getCurrentPathForTracking } = useCurrentPathForTracking();

// 契約状態
const { data: teamInfo } = useTeamInfo();
const enableUserDocument = computed(
  () => teamInfo.value?.enable_user_document ?? false,
);

// フィルターの開閉状態と操作
const isOpen = ref(false);
const openModal = () => (isOpen.value = true);

// フォームの値と操作
const fileSearchQuery = ref('');
const isComposing = ref(false); // IMEの変換状態を管理
const page = ref(1);
const foundedFiles = ref<Array<FileNameSearchResponse>>([]);
const hasMoreFiles = ref(true);
const modalBodyRef = ref<HTMLElement | null>();
const fileSearchInputRef = ref<HTMLInputElement | null>(null);
const specifiedFiles = defineModel('specifiedFiles', {
  default: [],
  required: true,
  type: Array<FileNameSearchResponse>,
});
const isLimited = computed(
  () => specifiedFiles.value.length >= SPECIFIED_FILES_LIMIT,
);
const isLoading = ref(false);
const isError = ref(false);
const errorMessage = ref('');

/* ファイル指定履歴と社内情報閲覧履歴 */
const {
  fetchLatestSpecifiedFileHistories,
  specifiedFileHistories,
  fetchLatestUserDocumentViewHistories,
  userDocumentViewHistories,
} = useUserDocumentActionHistories(userSession.getUserId());

// ファイル指定の方法
const specifiedMethod = inject(
  specifiedMethodKey,
  ref<SpecifiedMethod | undefined>(undefined),
);

watch(
  fileSearchQuery,
  debounce(async () => {
    if (isComposing.value) return;

    initializeState();
    if (fileSearchQuery.value.trim().length !== 0)
      await fileNameSearch(fileSearchQuery.value, page.value, PAGING_LIMIT);
  }, FILE_NAME_SEARCH_API_DEBOUNCE_WAIT_MS),
);

let observer: IntersectionObserver;
watch([isOpen, modalBodyRef], async ([isOpenVal, modalBodyVal]) => {
  if (isOpenVal && modalBodyVal) {
    await Promise.all([
      fetchLatestUserDocumentViewHistories(),
      fetchLatestSpecifiedFileHistories(),
    ]);

    const filesEl = modalBodyVal.querySelector('.files') as HTMLElement | null;
    if (!filesEl) return;

    observer = new IntersectionObserver(fetchMoreFiles, {
      root: filesEl,
      rootMargin: '0px',
      threshold: 0,
    });

    const sentinel = filesEl.querySelector('#sentinel');
    if (sentinel) {
      observer.observe(sentinel);
    }

    if (fileSearchInputRef.value) fileSearchInputRef.value.focus();
  }
});

onBeforeUnmount(() => {
  if (observer) observer.disconnect();
});

/**
 * Handlers
 */

const onInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target) fileSearchQuery.value = target.value;
};

const onCompositionStart = () => (isComposing.value = true);

const onCompositionEnd = async () => {
  isComposing.value = false;
  // IME確定時には、1度ファイル検索APIを呼び出す
  if (fileSearchQuery.value.trim().length !== 0) {
    initializeState();
    await fileNameSearch(fileSearchQuery.value, page.value, PAGING_LIMIT);
  }
};

async function fileNameSearch(query: string, page: number, limit: number) {
  try {
    isLoading.value = true;

    const newFiles = await api.fileNameSearch(query, page, limit);
    if (newFiles.length === 0 || newFiles.length < limit)
      hasMoreFiles.value = false;

    foundedFiles.value = [...foundedFiles.value, ...newFiles];
  } catch (error) {
    isError.value = true;
    if (isAxiosError(error) && error.response?.status === 404) {
      errorMessage.value =
        '社内情報ストレージに同期されたファイルが見つかりません。';
    } else {
      errorMessage.value =
        'エラーが発生しました。再度キーワードを入れ直してください。';
    }
    throw new Error(`ファイル検索に失敗しました: ${error}`);
  } finally {
    isLoading.value = false;
  }
}

const selectFile = async (
  file: FileNameSearchResponse,
  method: SpecifiedMethod,
) => {
  if (isLimited.value) return;
  if (specifiedFiles.value.some(f => f.id === file.id)) return;
  specifiedFiles.value.push(file);
  specifiedMethod.value = method;
  isOpen.value = false;

  emit('select-file');

  // 計測
  await trackingSurveyEvent('document_specify', {
    page: {
      name: props.pageName,
      url: getCurrentPathForTracking(),
    },
    specified_method: method,
    document: {
      type: 'user_document',
      id: file.id,
    },
    session_id: props.sessionId ?? null,
  });
};

const fetchMoreFiles = async (entries: IntersectionObserverEntry[]) => {
  if (isLoading.value) return;
  if (foundedFiles.value.length === 0 || !hasMoreFiles.value) return;

  const entry = entries[0];
  if (entry.isIntersecting) {
    if (!isError.value) page.value++;
    await fileNameSearch(fileSearchQuery.value, page.value, PAGING_LIMIT);
  }
};

const clearFileSearchQuery = () => (fileSearchQuery.value = '');

const initializeState = () => {
  foundedFiles.value = [];
  page.value = 1;
  hasMoreFiles.value = true;
  isError.value = false;
};
</script>

<template>
  <button
    class="file-specific-button"
    :class="{ 'disable-file-specific': !enableUserDocument || isLimited }"
    @click="openModal"
    :disabled="!enableUserDocument || isLimited"
  >
    <DgrIcon
      name="file-select"
      size="small"
      :keep-fill="false"
      class="file-specific-icon"
      :class="{ 'disable-file-specific': !enableUserDocument || isLimited }"
    />
    <span class="c-text-regular--m">ファイルを指定</span>
  </button>

  <DgrDialog
    id="file-name-search-modal"
    v-model:open="isOpen"
    title="ファイルを指定"
    :modal="false"
    :has-footer="false"
  >
    <template #description>
      <div class="modal-body" ref="modalBodyRef">
        <div class="input-container">
          <DgrIcon name="search" size="small" class="search-icon" />
          <input
            ref="fileSearchInputRef"
            class="file-search-input m-input c-text-regular--m c-textInput"
            placeholder="ファイル名を入力してください"
            @input="onInput"
            @compositionstart="onCompositionStart"
            @compositionend="onCompositionEnd"
            :value="fileSearchQuery"
          />
          <button
            v-if="fileSearchQuery.trim().length !== 0"
            @click="clearFileSearchQuery"
            class="clear-button c-text-regular--xs"
            aria-label="入力内容を削除"
          >
            入力をクリア
          </button>
        </div>
        <div class="files">
          <SurveySimpleUserDocumentItem
            v-for="file in foundedFiles"
            :key="file.id"
            :file="file"
            :selected="specifiedFiles.some(f => f.id === file.id)"
            @select-file="file => selectFile(file, 'document_search')"
          />
          <div id="sentinel"></div>
          <DgrLoading v-if="isLoading" class="loading-spinner" />
          <div
            v-if="foundedFiles.length > 0 && !hasMoreFiles && !isLoading"
            class="no-more-files-message c-text-regular--s"
          >
            これ以上、表示できるファイルはありません。
          </div>
        </div>
        <div
          v-if="
            fileSearchQuery.trim().length === 0 && foundedFiles.length === 0
          "
          class="user-document-action-histories"
        >
          <div
            v-if="specifiedFileHistories.length > 0"
            class="histories-container"
          >
            <p class="history-title c-text-regular--xs">
              <span>指定履歴</span>
            </p>
            <SurveySimpleUserDocumentItem
              v-for="history in specifiedFileHistories"
              :key="`specified-file-history-${history.id}`"
              :file="history"
              :selected="specifiedFiles.some(f => f.id === history.id)"
              @select-file="file => selectFile(file, 'specified_history')"
            />
          </div>
          <div
            v-if="userDocumentViewHistories.length > 0"
            class="histories-container"
          >
            <p class="history-title c-text-regular--xs">
              <span>閲覧履歴</span>
            </p>
            <SurveySimpleUserDocumentItem
              v-for="history in userDocumentViewHistories"
              :key="`user-document-view-history-${history.id}`"
              :file="history"
              :selected="specifiedFiles.some(f => f.id === history.id)"
              @select-file="file => selectFile(file, 'view_history')"
            />
          </div>
        </div>
        <div
          v-if="
            fileSearchQuery &&
            foundedFiles.length === 0 &&
            !hasMoreFiles &&
            !isLoading
          "
          class="no-hit-files-message c-text-regular--s"
        >
          <span>{{ fileSearchQuery }}</span>
          に一致する検索結果は見つかりませんでした。
        </div>
        <div v-else-if="isError" class="error-message c-text-regular--s">
          {{ errorMessage }}
        </div>
        <div class="file-limit-label c-text-regular--s">
          10件までファイルを指定できます。
        </div>
      </div>
    </template>
  </DgrDialog>
</template>

<style lang="scss" scoped>
.file-specific-button {
  display: flex;
  align-items: center;
  color: $color-gray1000;
  background: #fff;
  height: 32px;
  border: 1px solid $color-border;
  width: auto;
  justify-content: space-between;
  padding: 4px 16px;
  gap: 4px;
}

.file-specific-icon {
  flex-shrink: 0;
  color: $color-gray1000;
}

.disable-file-specific {
  color: $color-gray400;
}

.modal-body {
  height: 480px;
  width: 565px;
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
}

.input-container {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 8px;
}

.file-search-input {
  width: 100%;
  padding: 8px 104px 8px 32px;
  border-radius: 4px;
}

.search-icon {
  position: absolute;
  left: 8px;
  color: $color-gray1000;
}

.clear-button {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px 16px;
  flex-shrink: 0;
  height: 24px;
  width: 92px;
  color: $color-gray800;
  &:hover {
    background-color: $color-gray400;
  }
}

.files,
.user-document-action-histories {
  overflow-y: scroll;
}

.histories-container {
  margin: 8px 0 8px;
}

.history-title {
  margin-bottom: 8px;
  color: $color-gray800;
}

.no-hit-files-message {
  margin: 8px 0;
  line-height: 16px;

  span {
    font-family: Noto Sans CJK JP;
    font-weight: 700;
  }
}

.no-more-files-message {
  margin: 16px auto;
  text-align: center;
  color: $color-gray600;
}

.error-message {
  margin: 8px 0;
  color: $color-orange1000;
}

.file-limit-label {
  margin-top: auto;
  padding-top: 16px;
  color: $color-gray1000;
}

#sentinel {
  height: 1px;
}

.loading-spinner {
  margin: 0 auto;
}
</style>
